import { Component, ComponentProps } from "../TSX/TSX";
import { ClassVariable, RegisterClassVariable, BasicEvent, EventInterface, ObjectUtils, BaseClass, MapWithDefault } from "@h4x/common";

class IntervalVariable {
	public name: string;
	constructor(public time: number) { }
	public register(variable: ClassVariable) {
		this.name = variable.name;
	}
}
const INTERVAL = ClassVariable.createType<IntervalVariable>("IntervalVariable");

class InitializeVariable {
	public name: string;
	constructor() { }
	public register(variable: ClassVariable) {
		this.name = variable.name;
	}
}
const INITIALIZE = ClassVariable.createType<InitializeVariable>("InitializeVariable");

class PropChangeFunction {
	public name: string;
	constructor(public prop: string) { }
	public register(variable: ClassVariable) {
		this.name = variable.name;
	}
}

const PROP_CHANGE_FN = ClassVariable.createType<PropChangeFunction>("PropChangeFunction");

class PropChangeVariable {
	public name: string;
	constructor(public prop: string) { }
	public register(variable: ClassVariable) {
		this.name = variable.name;
	}
}

const PROP_CHANGE = ClassVariable.createType<PropChangeVariable>("PropChangeVariable");


export type $Proxy<T> = {
	get: () => T;
	set: (variable: T) => void;
	onUpdate: BasicEvent<(value?: T) => void>;
	onChange: BasicEvent<(value?: T) => void>;
};

export abstract class BasicComponent<P = {}> extends Component<P> {
	private attachedEventReferences: EventInterface<any, any>[] = [];

	protected onDestroy = new BasicEvent<() => void>();

	constructor() {
		super();
	}

	public proxy<K extends keyof this = keyof this>(key: K): $Proxy<this[K]> {
		return ObjectUtils.$(this, key);
	}

	public $<K extends keyof this = keyof this>(key: K): $Proxy<this[K]> {
		return ObjectUtils.$(this, key);
	}

	public static var() {
		return (target: any, key: string) => {
			let internalVar = Symbol.for("var__" + key);
			Object.defineProperty(target, key, {
				get: function (this: any) {
					return this[internalVar];
				},
				set: function (this: any, value: any) {
					this[internalVar] = value;
					this.$(key).onUpdate.execute(value);
				},
				enumerable: true
			});
		};
	}

	public static override bind<T extends BasicComponent<any>>() {
		return (target: T, key: string): void => {
			if (target instanceof BasicComponent === false) {
				throw new Error("Failed to bind, class needs to be a subclass of BasicComponent!");
			}

			return ObjectUtils.bind()(target, key);
		};
	}

	public static interval<T extends BasicComponent<any>>(time: number) {
		return function (target: T, key: string) {
			if (target instanceof BasicComponent === false) {
				throw new Error("Failed to attach interval, class needs to be a subclass of BasicComponent!");
			}
			RegisterClassVariable(target.constructor, key).attachData(INTERVAL, new IntervalVariable(time));
		};
	}

	public static initialize<T extends BasicComponent<any>>() {
		return function (target: T, key: string) {
			if (target instanceof BasicComponent === false) {
				throw new Error("Failed to attach initialize, class needs to be a subclass of BasicComponent!");
			}
			RegisterClassVariable(target.constructor, key).attachData(INITIALIZE, new InitializeVariable());
		};
	}


	// TODO: require function (value: T["props"][prop], oldValue: T["props"][prop] | undefined) => void
	public static propChange<T extends BasicComponent<any>, P extends keyof T["props"]>(prop: P) {
		return function (target: T, key: string) {
			if (target instanceof BasicComponent === false) {
				throw new Error("Failed to attach prop change, class needs to be a subclass of BasicComponent!");
			}
			RegisterClassVariable(target.constructor, key).attachData(PROP_CHANGE_FN, new PropChangeFunction(prop as any));
		};
	}

	public static prop<T extends BasicComponent<any>, P extends keyof T["props"]>(prop: P) {
		return function (target: T, key: string) {
			if (target instanceof BasicComponent === false) {
				throw new Error("Failed to attach prop change, class needs to be a subclass of BasicComponent!");
			}
			RegisterClassVariable(target.constructor, key).attachData(PROP_CHANGE, new PropChangeVariable(prop as any));
		};
	}

	private __propsHandlers = new MapWithDefault<string, BasicEvent<(value: any, oldValue: any) => void>>(() => new BasicEvent());
	private __initProps() {
		this[BaseClass.Meta].forEachVariable((variable, key) => {
			let propChangeData = variable.getData(PROP_CHANGE_FN)!;
			if (propChangeData !== undefined) {
				this.__propsHandlers.get(propChangeData.prop).addCallback((value, oldValue) => {
					(this as any)[key](value, oldValue);
				});
			}

			let propData = variable.getData(PROP_CHANGE)!;
			if (propData !== undefined) {
				this.__propsHandlers.get(propData.prop).addCallback((value, oldValue) => {
					(this as any)[key] = value;
				});
			}
		});
	}

	private __propsChanged(oldProps: ComponentProps<P, this>, props: ComponentProps<P, this>) {
		for (const [key, handlers] of this.__propsHandlers.entries()) {
			let value = (props as any)[key];
			let oldValue = (oldProps as any)[key];
			if (value !== oldValue) {
				handlers.execute(value, oldValue);
			}
		}
	}

	private __init() {
		this[BaseClass.Meta].forEachVariable((variable, key) => {
			let intervalData = variable.getData(INTERVAL)!;
			if (intervalData !== undefined) {
				// intervalData.time
				let interval = window.setInterval(() => {
					(this as any)[key]();
				}, intervalData.time);

				this.onDestroy.addCallback(() => {
					if (interval !== undefined) {
						clearInterval(interval);
					}
				});
			}

			let initializeData = variable.getData(INITIALIZE)!;
			if (initializeData !== undefined) {
				(this as any)[key]();
			}
		});

		this.__initProps();
	}

	private __destroy() {
		this.onDestroy.execute();
		for (const ref of this.attachedEventReferences) {
			ref.remove();
		}
	}

	/** Attach event reference to be cleaned up when the object is destroyed */
	protected ref<T extends EventInterface<any, any>>(ref: T) {
		this.attachedEventReferences.push(ref);
		return ref;
	}

	public static normalizeClass(target: any): undefined | { [K: string]: boolean } {
		if (Array.isArray(target)) {
			let out: any = {};
			for (const cls of target) {
				out[cls] = true;
			}
			return out;
		} else if (typeof target === "object") {
			return target;
		} else if (typeof target === "string") {
			let out: any = {};
			for (const cls of target.split(" ")) {
				out[cls] = true;
			}
			return out;
		}
		return undefined;
	}

	public static mergeClasses(
		...target: (string | { [K: string]: boolean | undefined } | (string | false | undefined | null)[] | undefined)[]
	): undefined | { [K: string]: boolean } {
		return Object.assign({}, ...target.map((cls) => BasicComponent.normalizeClass(cls)));
	}
}
